import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { getTextOverrides } from '@/lib/appConfig';
import { FIXED_LANGUAGE_PROCESSOR, fixedLanguagePostProcessor } from '@/lib/fixedLanguageKeys';
import { flattenOverrides, pickForLanguage } from '@/lib/textOverrides';
import { IS_APP_BUILD } from '@/lib/appBrand';

import kk from '@/locales/kk.json';
import ru from '@/locales/ru.json';
import en from '@/locales/en.json';

export type Lang = 'kk' | 'ru' | 'en';

const LANGS: readonly Lang[] = ['kk', 'ru', 'en'];

/**
 * Языки интерфейса: казахский, русский, английский — переводы в
 * `src/locales/{kk,ru,en}.json`.
 *
 * ⚠️ ПО УМОЛЧАНИЮ — РУССКИЙ (решение владельца 24.09.2026), на сайте и в приложении
 * из магазина. Им же закрываются строки, которых нет в выбранном языке.
 *
 * История, чтобы не «починили» обратно:
 * - до 24.08.2026 — казахский;
 * - 24.08–24.09.2026 — АНГЛИЙСКИЙ, ради проверяющего Apple: он открывает чистую
 *   установку и должен прочитать первый экран. ⚠️ Поэтому в Review Notes к сборке 61
 *   (и дальше) нужно написать, где переключить язык на English: на экране входа
 *   (ҚАЗ / РУС / ENG) и в профиле.
 *
 * Свой выбор человека всегда побеждает: он лежит в `localStorage.lang` и здесь
 * не переписывается и не сбрасывается — даже если там что-то незнакомое.
 *
 * Сознательно НЕ смотрим:
 * - язык браузера — решение владельца: база одна, русская;
 * - `auth.users.lang` с сервера — он у всех `ru` по умолчанию, меняется только из
 *   профиля; письма идут по нему, интерфейс — по выбору на этом устройстве.
 * `VITE_DEFAULT_LOCALE` больше не читается: локальный `.env` с `en` тихо
 * возвращал бы английский в сборку.
 */
export const DEFAULT_LANG: Lang = 'ru';

/** Стартовый язык: сохранённый выбор, если он из трёх наших, иначе русский. */
export function pickStartLanguage(stored: string | null): Lang {
  return LANGS.includes(stored as Lang) ? (stored as Lang) : DEFAULT_LANG;
}

/**
 * `<html lang>` — по языку интерфейса. Жёсткий `kk` в index.html заставлял Edge и
 * Chrome предлагать русскоязычному ученику «перевести с казахского», а перевод
 * страницы ронял кабинет (техдолг 263).
 */
function syncHtmlLang(lng: string) {
  if (typeof document !== 'undefined') document.documentElement.lang = lng;
}

export function initI18n() {
  const lng = pickStartLanguage(localStorage.getItem('lang'));

  // ⚠️ Часть текстов показывается на одном языке при любом интерфейсе — например, весь
  // блок записи голоса всегда по-английски (решение владельца 15.09.2026). Это не
  // потерянный перевод: правила и причина — `lib/fixedLanguageKeys.ts`.
  i18n.use(initReactI18next).use(fixedLanguagePostProcessor).init({
    resources: {
      kk: { translation: kk },
      ru: { translation: ru },
      en: { translation: en },
    },
    lng,
    fallbackLng: DEFAULT_LANG,
    interpolation: { escapeValue: false },
    postProcess: [FIXED_LANGUAGE_PROCESSOR],
    // ⚠️ Страховка приложения (13.10.2026): строки с «тариф» вырезаются из сборки приложения (vite.config.ts,
    // решение владельца), а их места на экранах закрыты `IS_APP_BUILD`. Пропущенное место показало бы имя ключа
    // («adminDetail.tariffTitle») — в приложении вместо него пусто. На сайте и в проверках поведение прежнее.
    ...(IS_APP_BUILD ? { parseMissingKeyHandler: () => '' } : {}),
  });
  syncHtmlLang(i18n.language);
  i18n.off('languageChanged', syncHtmlLang);
  i18n.on('languageChanged', syncHtmlLang);
}

/**
 * Накладывает правки текстов, присланные сервером, поверх встроенных.
 *
 * ⚠️ ЗАЧЕМ. Тексты лежат ВНУТРИ сборки: правка на сайте до приложения не
 * доходит, нужна новая сборка и неделя проверки в App Store. Здесь сервер
 * может поправить любую строку сразу и у всех.
 *
 * ⚠️ ТОЛЬКО ПОВЕРХ, НИКОГДА ВМЕСТО. Не прислали ключ — работает встроенный
 * текст. Значит недоступный сервер настроек не может оставить приложение с
 * пустыми экранами.
 */
export function applyTextOverrides(): void {
  const overrides = getTextOverrides();
  for (const lng of ['kk', 'ru', 'en'] as const) {
    const flat = pickForLanguage(overrides, lng);
    if (Object.keys(flat).length === 0) continue;
    // deep = true, overwrite = true: добавляем к дереву переводов, заменяя
    // только присланные ключи.
    i18n.addResourceBundle(lng, 'translation', flattenOverrides(flat), true, true);
  }
  // Перерисовать уже открытый экран: правка должна дойти без перезапуска.
  i18n.changeLanguage(i18n.language);
}

export function setLanguage(lng: Lang) {
  localStorage.setItem('lang', lng);
  i18n.changeLanguage(lng);
}

export default i18n;
